// Timer query and mutations (architecture sprint-04 §5.3, §5.4). Every response carrying a TimerDto
// becomes the one cached snapshot under ['timer']; stop (saved / nothing_saved) and discard set null.
// Mutations are silent: TimerActionsProvider maps their errors (§5.7).
import { useMutation, useQuery, useQueryClient, type QueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import { LIVE_REFETCH_MS } from '@shared/report-rules';
import { useInvalidateEntryData } from '@/hooks/use-entries';
import type { AlarmStateDto } from '@shared/alarm-rules';
import { setAlarmSnapshot } from '@/hooks/use-alarm';
import { setShiftSnapshot } from '@/hooks/use-shifts';
import type { ShiftDto, ShiftNoticeDto } from '@/types/shifts';
import { apiRequest, apiRequestNoContent } from '@/lib/api';
import { broadcast } from '@/lib/sync';
import type { StartBody, StartDefaults, StopResult, SwitchBody, SwitchResult, TimerDto, TimerSnapshot } from '@/types/timer';

export const TIMER_QUERY_KEY = ['timer'] as const;
const TIMER_STALE_MS = 5_000;
const QUICK_START_KEY = ['entries', 'quick-start'] as const;

interface TimerGetResponse {
  timer: TimerDto | null;
  shift?: ShiftDto | null;
  shiftNotice?: ShiftNoticeDto | null;
  /** Sprint 14: the own check-in alarm state (§7.8). */
  alarm?: AlarmStateDto | null;
  /** Sprint 14: the server clock at the response. */
  serverNow?: string;
}

export function toSnapshot(timer: TimerDto): TimerSnapshot {
  return { timer, receivedAt: performance.now() };
}

export function setTimerSnapshot(queryClient: QueryClient, timer: TimerDto | null): void {
  queryClient.setQueryData<TimerSnapshot | null>(TIMER_QUERY_KEY, timer ? toSnapshot(timer) : null);
}

/** Polls every 30 s even in hidden tabs, so a stop on another device clears within 30 s (F50). */
export function useTimer(): UseQueryResult<TimerSnapshot | null> {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: TIMER_QUERY_KEY,
    queryFn: async () => {
      const res = await apiRequest<TimerGetResponse>('/api/timer');
      // Sprint 13 (§14.1): the open shift and the auto-close notice ride along (absent on an older server).
      // Sprint 14: the top-level serverNow (owner decision 2026-10-28) is the server clock even with no timer.
      setShiftSnapshot(queryClient, res.shift ?? null, res.shiftNotice ?? null, res.serverNow ?? res.timer?.serverNow);
      setAlarmSnapshot(queryClient, res.alarm ?? null);
      return res.timer ? toSnapshot(res.timer) : null;
    },
    refetchInterval: LIVE_REFETCH_MS,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    staleTime: TIMER_STALE_MS,
  });
}

/** Fetched fresh whenever the Start-with-details dialog opens. */
export function useStartDefaults(enabled: boolean): UseQueryResult<StartDefaults> {
  return useQuery({
    queryKey: ['timer', 'start-defaults'],
    queryFn: () => apiRequest<StartDefaults>('/api/timer/start-defaults'),
    enabled,
    staleTime: 0,
  });
}

function post<T>(path: string, body: unknown): Promise<T> {
  return apiRequest<T>(path, { method: 'POST', body: JSON.stringify(body) });
}

export function useStartTimer(): UseMutationResult<{ timer: TimerDto }, Error, StartBody> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => post<{ timer: TimerDto }>('/api/timer/start', body),
    meta: { silent: true },
    onSuccess: async ({ timer }) => {
      setTimerSnapshot(queryClient, timer);
      broadcast({ type: 'timer-changed' });
      await queryClient.invalidateQueries({ queryKey: QUICK_START_KEY });
    },
  });
}

function useVersionAction(path: string): UseMutationResult<{ timer: TimerDto }, Error, number> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (version) => post<{ timer: TimerDto }>(path, { version }),
    meta: { silent: true },
    onSuccess: ({ timer }) => {
      setTimerSnapshot(queryClient, timer);
      broadcast({ type: 'timer-changed' });
    },
  });
}

export function usePauseTimer(): UseMutationResult<{ timer: TimerDto }, Error, number> {
  return useVersionAction('/api/timer/pause');
}

export function useResumeTimer(): UseMutationResult<{ timer: TimerDto }, Error, number> {
  return useVersionAction('/api/timer/resume');
}

export function useSwitchTimer(): UseMutationResult<SwitchResult, Error, SwitchBody & { version: number }> {
  const queryClient = useQueryClient();
  const invalidateEntries = useInvalidateEntryData();
  return useMutation({
    mutationFn: (body) => post<SwitchResult>('/api/timer/switch', body),
    meta: { silent: true },
    onSuccess: async (result) => {
      setTimerSnapshot(queryClient, result.timer);
      broadcast({ type: 'timer-changed' });
      await invalidateEntries();
    },
  });
}

export function useStopTimer(): UseMutationResult<StopResult, Error, { version: number; midnight?: 'split' | 'keep' }> {
  const queryClient = useQueryClient();
  const invalidateEntries = useInvalidateEntryData();
  return useMutation({
    mutationFn: (body) => post<StopResult>('/api/timer/stop', body),
    meta: { silent: true },
    onSuccess: async (result) => {
      if (result.outcome === 'decision_required') {
        // No write happened, so the version is unchanged; refresh the display only.
        setTimerSnapshot(queryClient, result.timer);
        return;
      }
      setTimerSnapshot(queryClient, null);
      broadcast({ type: 'timer-changed' });
      await invalidateEntries();
    },
  });
}

export function useDiscardTimer(): UseMutationResult<void, Error, number> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (version) => apiRequestNoContent(`/api/timer?version=${version}`, { method: 'DELETE' }),
    meta: { silent: true },
    onSuccess: async () => {
      setTimerSnapshot(queryClient, null);
      broadcast({ type: 'timer-changed' });
      await queryClient.invalidateQueries({ queryKey: QUICK_START_KEY });
    },
  });
}
